import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { AlertTriangle, Clock, Sparkles, X, Trash2 } from 'lucide-react';

export const TrialBanner = () => {
  const { subscription } = useSubscription();
  const [dismissed, setDismissed] = useState(false);

  if (!subscription || dismissed) return null;
  
  // Show banner for trial users OR if trial has expired
  const isOnTrial = subscription.is_trial || subscription.subscription_status === 'trial';
  const isExpired = subscription.trial_expired;
  const daysLeft = subscription.trial_days_remaining || 0;
  
  // Don't show if not on trial and not expired
  if (!isOnTrial && !isExpired) return null;
  
  // Don't show if trial has more than 7 days remaining (unless expired)
  if (!isExpired && daysLeft > 7) return null;

  // Get plan name for display
  const planName = subscription.plan_name || subscription.tier_name || 'your plan';

  return (
    <div 
      className={`px-4 py-3 flex items-center justify-between ${
        isExpired 
          ? 'bg-red-50 border-b border-red-200' 
          : 'bg-amber-50 border-b border-amber-200'
      }`}
      data-testid="trial-banner"
    >
      <div className="flex items-center gap-3">
        {isExpired ? (
          <AlertTriangle className="w-5 h-5 text-red-500" />
        ) : (
          <Clock className="w-5 h-5 text-amber-500" />
        )}
        <span className={isExpired ? 'text-red-700' : 'text-amber-700'}>
          {isExpired 
            ? `Your 14-day trial has ended. Upgrade to keep using ${planName} features.`
            : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left in your free trial of ${planName}.`
          }
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          className="bg-[#2C5F7C] hover:bg-[#1E4A60] text-white rounded-full px-4"
          onClick={() => window.location.href = '/settings?tab=subscription'}
          data-testid="upgrade-btn"
        >
          <Sparkles className="w-4 h-4 mr-1" />
          Upgrade Now
        </Button>
        {!isExpired && (
          <button
            onClick={() => setDismissed(true)}
            className="text-amber-500 hover:text-amber-700"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export const DemoBanner = () => {
  return null;
};

export const UpgradePrompt = ({ feature, currentTier }) => {
  const tierInfo = {
    home_baker: { name: 'Home Baker', price: '$12.99' },
    pro_baker: { name: 'Pro Baker', price: '$19.99' }
  };

  const featureToTier = {
    labour_overhead: 'home_baker',
    retail_pricing: 'home_baker',
    recipe_scaling: 'home_baker',
    wholesale_pricing: 'pro_baker',
    csv_export: 'pro_baker'
  };

  const requiredTier = featureToTier[feature] || 'pro_baker';
  const tier = tierInfo[requiredTier];

  return (
    <div 
      className="bg-gradient-to-r from-[#EFF6FF] to-[#F3E8EC] border border-[#DBEAFE] rounded-xl p-6 text-center"
      data-testid="upgrade-prompt"
    >
      <Sparkles className="w-10 h-10 text-[#9B7EBD] mx-auto mb-3" />
      <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Unlock this feature
      </h3>
      <p className="text-[#6B7280] mb-4">
        This feature is available on the {tier.name} plan ({tier.price}/month).
      </p>
      <Button
        className="bg-[#2C5F7C] hover:bg-[#1E4A60] text-white rounded-full px-6"
        onClick={() => window.location.href = '/settings?tab=subscription'}
      >
        View Plans
      </Button>
    </div>
  );
};

export const LimitReachedBanner = ({ type, current, max }) => {
  return (
    <div 
      className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between"
      data-testid="limit-reached-banner"
    >
      <div className="flex items-center gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-500" />
        <span className="text-amber-700">
          You've reached your {type} limit ({current}/{max}). Upgrade for more.
        </span>
      </div>
      <Button
        size="sm"
        className="bg-[#2C5F7C] hover:bg-[#1E4A60] text-white rounded-full px-4"
        onClick={() => window.location.href = '/settings?tab=subscription'}
      >
        Upgrade
      </Button>
    </div>
  );
};

export const SampleBadge = () => {
  return null;
};
